import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-05-25 21:10:12 +0200 (Mon, 25 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163262 $
//  Purpose   : expression columns
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Label } from 'semantic-ui-react';
// ---Framework imports
import { get, keys, uniq, isNil, mapItems, size, filterItems, dict, sorted, flatten, values } from 'ldash';
import { Actions } from 'uix/registries';
import { XMenu, Editor, SplitPane } from 'uix/components';
import { useState, useMemo, useEffect, useResettableState, useCallback, useDebounce, usePersistent, useActions } from 'uix/hooks';
import log from 'uix/logging';
import { MIME } from 'uix/utils';
import { JSONTree } from 'uix/chunks';
import { hashObj } from 'uix/utils';
// ---Local imports
import { registerColumns, ColumnsById, unregisterColumns } from './columns';
import { ColumnTypeNames } from './columnTypes';
// ---Globals
const useRegistrations = ()=>usePersistent('ExpressionColumns', {});
const FIELD_RE = /\$([a-zA-Z_]\w*)/g;
const BAD_FUNC_EXPRS = {};
//~ const ERR_FUNC_EXPRS = {}
const buildExpressionFunc = (expr)=>{
    expr = expr.replace(FIELD_RE, (_, path)=>`get(row, ${JSON.stringify(path)})`);
    if (!BAD_FUNC_EXPRS[expr]) {
        try {
            //~ ERR_FUNC_EXPRS[expr] = 0
            let fn = new Function('row', 'get', `return (${expr});`);
            return {
                func: (row)=>{
                    try {
                        return fn(row, get);
                    } catch (e) {
                        // bad function application
                        return '[err]';
                    }
                },
                error: null,
                expr
            };
        } catch (e) {
            BAD_FUNC_EXPRS[expr] = String(e);
        //~ console.error('Error constructing func:', e)
        }
    }
    // error case
    return {
        func: (row)=>'err',
        error: BAD_FUNC_EXPRS[expr],
        expr
    };
};
const no_ns_fields = new Set([
    'otparent',
    'otid',
    'odid',
    'datatype',
    'subtype',
    'caption',
    'delstate'
]);
const useKnownFields = (availableColumns)=>useMemo(()=>{
        let allowed = size(availableColumns) > 0 ? new Set(availableColumns) : 'all';
        return new Set(flatten(mapItems(ColumnsById, (n, col)=>size(col.fields) ? col.fields : [
                n
            ], (n)=>!n.startsWith('$expression.') && (allowed == 'all' || allowed.has(n) || no_ns_fields.has(n)))));
    }, [
        ColumnsById.length,
        availableColumns
    ]);
const extractFieldsFromExpressionFunc = (text, known_fields)=>{
    /* undo namespace underscore normalization to from data field names
  $oor_test2_charconf  -> oor_test.charconf
  $oor_charconf  -> oor.charconf
  */ // sort desc by length, longer namespaces will be tested first
    let known_ns = sorted(uniq(keys(ColumnsById).map((n)=>n.includes('.') ? n.split('.')[0] : null).filter(Boolean)), (e)=>-e.length);
    return (text.match(FIELD_RE) || []).map((n)=>n.slice(1)).map((n)=>{
        let field = n;
        // no_ns_fields are always known and need no ns handling
        if (!no_ns_fields.has(field)) {
            // check for known namespace prefix
            let found_ns = known_ns.find((ns)=>n.startsWith(ns));
            if (found_ns) {
                field = `${found_ns}.${n.slice(found_ns.length + 1)}`;
            }
            if (!known_fields.has(field)) {
                log.warn('unkown ' + field);
                field = null;
            }
        }
        return field;
    }).filter(Boolean);
};
export const useCustomExpressionRegistration = (colids)=>{
    let [expressionColumns] = useRegistrations();
    let col_name = (colid)=>'$expression.' + colid + '_' + hashObj([
            expressionColumns[colid].label,
            expressionColumns[colid].expression
        ]);
    let activeCols = mapItems(expressionColumns, (colid)=>colid, (colid, coldef)=>coldef.active);
    let new_col_names = activeCols.map((colid)=>col_name(colid));
    let [activated_col, set_activated_cols] = useState([]);
    useEffect(()=>{
        let old_regs = keys(ColumnsById).filter((colid)=>colid.startsWith('$expression.'));
        unregisterColumns(old_regs);
        let new_regs = activeCols.map((colid)=>{
            let coldef = expressionColumns[colid];
            let header = coldef.label || colid;
            if (coldef.expressionError) {
                header = `${header} (${coldef.expressionError.split(':').at(0)})`;
            }
            return [
                ColumnTypeNames.NumberColumn,
                col_name(colid),
                {
                    accessor: buildExpressionFunc(coldef.expression).func,
                    fields: coldef.fields,
                    Header: header
                }
            ];
        });
        registerColumns(new_regs);
        set_activated_cols(new_col_names);
    }, [
        new_col_names.join(',')
    ]);
    return [
        ...colids,
        ...activated_col
    ];
};
const initialColDef = {
    name: null,
    label: '',
    active: false,
    expression: '',
    expressionError: null,
    fields: []
};
const ExpressionFilter = ({ rows, columns, updateColumn, deleteColumn, availableColumns })=>{
    let successNotification = useActions(Actions.successNotification);
    // colid of selected column (in editing)
    let [selectedCol, setSelectedCol] = useState(null);
    if (!selectedCol && size(columns)) {
        selectedCol = values(columns)[0].name;
    }
    let [expression, setExpression] = useResettableState({
        text: null,
        colid: null
    }, [
        selectedCol
    ]);
    let [debounced_expression] = useDebounce(expression, 1000);
    let known_fields = useKnownFields(availableColumns);
    let known_fields_list = useMemo(()=>[
            ...known_fields
        ].map((n)=>'$' + n.replace('.', '_')), [
        known_fields
    ]);
    useEffect(()=>{
        if (debounced_expression.colid) {
            let f_text = debounced_expression.text;
            let res = buildExpressionFunc(f_text);
            updateColumn(debounced_expression.colid, {
                expression: f_text,
                expressionError: res.error,
                fields: res.error ? [] : extractFieldsFromExpressionFunc(f_text, known_fields)
            });
        }
    }, [
        debounced_expression
    ]);
    let activeColDef = columns[selectedCol] || initialColDef;
    let collist = mapItems(columns, (colid, coldef)=>/*#__PURE__*/ _jsx("div", {
            style: {
                backgroundColor: selectedCol == colid ? '#AAA' : undefined,
                cursor: 'pointer'
            },
            onClick: ()=>setSelectedCol(colid),
            children: columns[colid].label || colid
        }, colid));
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs(SplitPane, {
            sizes: [
                0.3,
                0.7
            ],
            vertical: true,
            persist: true,
            borderless: true,
            name: "exprcolsp1",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs(XMenu, {
                            size: "mini",
                            borderless: true,
                            attached: true,
                            style: {
                                width: '100%'
                            },
                            children: [
                                /*#__PURE__*/ _jsx(XMenu.Item, {
                                    icon: "plus",
                                    onClick: ()=>{
                                        let new_name = `cust${Math.max(...[
                                            0,
                                            ...keys(columns).map((e)=>parseInt(e.slice(4)))
                                        ]) + 1}`;
                                        updateColumn(new_name, {
                                            name: new_name
                                        });
                                        setSelectedCol(new_name);
                                    }
                                }),
                                /*#__PURE__*/ _jsx(XMenu.Item, {
                                    icon: "minus",
                                    onClick: ()=>{
                                        deleteColumn(selectedCol);
                                        setSelectedCol(null);
                                    },
                                    disabled: !selectedCol,
                                    confirm: `Delete "${columns[selectedCol]?.label || selectedCol}"`
                                }),
                                /*#__PURE__*/ _jsx(XMenu.Input, {
                                    fluid: true,
                                    fitted: true,
                                    debounce: 300,
                                    value: activeColDef.label,
                                    onValueChange: (value)=>updateColumn(activeColDef.name, {
                                            label: value
                                        }),
                                    placeholder: activeColDef.name,
                                    disabled: !selectedCol
                                }),
                                /*#__PURE__*/ _jsx(XMenu.BeginRight, {}),
                                /*#__PURE__*/ _jsx(XMenu.Toggle, {
                                    icon: "check",
                                    content: "Use",
                                    value: activeColDef.active,
                                    set: ()=>updateColumn(activeColDef.name, {
                                            active: !activeColDef.active
                                        }),
                                    disabled: !selectedCol
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            children: collist
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(SplitPane, {
                    sizes: [
                        0.3,
                        0.7
                    ],
                    persist: true,
                    borderless: true,
                    flexInner: true,
                    name: "exprcolsp2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            style: {
                                flex: 1,
                                display: 'flex'
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Editor, {
                                    flex: true,
                                    mimetype: MIME.JS,
                                    text: expression.text ?? activeColDef.expression,
                                    onBeforeChange: ({ value })=>setExpression({
                                            text: value,
                                            colid: selectedCol
                                        }),
                                    opts: {
                                        readOnly: !selectedCol ? 'nocursor' : false
                                    },
                                    customHighlightWords: known_fields_list
                                }),
                                activeColDef.expressionError && /*#__PURE__*/ _jsx(Label, {
                                    color: "orange",
                                    size: "small",
                                    style: {
                                        position: 'absolute',
                                        bottom: 0,
                                        zIndex: 10
                                    },
                                    title: buildExpressionFunc(expression.text ?? activeColDef.expression).expr,
                                    children: activeColDef.expressionError
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            style: {
                                flex: 1,
                                display: 'flex',
                                overflow: 'auto',
                                backgroundColor: 'rgb(0, 43, 54)',
                                lineHeight: '1.0em'
                            },
                            children: /*#__PURE__*/ _jsx(JSONTree, {
                                hideRoot: true,
                                data: rows,
                                compact: true,
                                labelRenderer: ([key])=>/*#__PURE__*/ _jsx("span", {
                                        onClick: ()=>{
                                            let var_name = '$' + key;
                                            navigator.clipboard.writeText(var_name);
                                            successNotification({
                                                message: `kopiert: "${var_name}"`
                                            });
                                        },
                                        children: key
                                    })
                            })
                        })
                    ]
                })
            ]
        })
    });
};
export const ExpressionFilterItem = ({ rows, availableColumns })=>{
    let [expressionColumns, setExpressionColumns] = useRegistrations();
    let updateColumn = useCallback((colid, data)=>setExpressionColumns({
            ...expressionColumns,
            [colid]: {
                ...expressionColumns[colid] || initialColDef,
                ...data
            }
        }), [
        expressionColumns,
        setExpressionColumns
    ]);
    let deleteColumn = useCallback((colid)=>setExpressionColumns(dict(filterItems(expressionColumns, (k)=>k != colid))), [
        expressionColumns,
        setExpressionColumns
    ]);
    return /*#__PURE__*/ _jsx(XMenu.DialogItem, {
        name: "ExpressionFilter",
        dlgHeader: "Columns (programmable)",
        icon: [
            {
                name: 'calculator',
                color: size(expressionColumns) ? 'green' : undefined
            },
            'text cursor'
        ],
        cornerContent: size(expressionColumns),
        title: "Programmierbare Spalten",
        children: /*#__PURE__*/ _jsx(ExpressionFilter, {
            rows: rows,
            updateColumn: updateColumn,
            deleteColumn: deleteColumn,
            columns: expressionColumns,
            availableColumns: availableColumns
        })
    });
};
